/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A QUIÉN SE ESCRIBE · el único sitio donde vive el contacto
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D37, datos del dueño el 05/09/2026. Antes de esto **no había ni una mención
 * de contacto en toda la plataforma**: ni un teléfono, ni un correo, ni la
 * palabra «soporte». Comprobado con `grep` en las tres aplicaciones y en el
 * diccionario, cero aciertos.
 *
 * Eso no es un olvido de interfaz. Un producto de salud que le dice a alguien
 * «esto hay que mirárselo, ve a un profesional» y no tiene detrás una forma de
 * escribirle a nadie es un producto al que no se le puede preguntar nada.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * POR QUÉ UN MÓDULO Y NO CUATRO CADENAS EN LA PÁGINA
 *
 * Porque estos cuatro datos van a salir en más de un sitio: hoy en el pie de
 * la página de venta, mañana en los cinco documentos legales, y el día que
 * haya correos de soporte, en su firma. Un teléfono escrito a mano en tres
 * pantallas es un teléfono que se cambia en dos.
 *
 * Y hay un caso concreto que este fichero existe para impedir, porque es el
 * que se ve poco y duele mucho: **que el número que se lee y el número al que
 * llama el enlace de WhatsApp dejen de ser el mismo**. Se lee «+57 311
 * 4551047», se pulsa, y se abre una conversación con otro número. Nadie lo
 * mira nunca porque el texto está bien. Aquí el enlace se CALCULA del número,
 * así que no pueden separarse, y `verificar:contacto` lo comprueba.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LO QUE ESTE FICHERO NO ES
 *
 * No es el contacto de los contratos. El dueño los editará desde el superadmin
 * cuando exista esa sección (D32), y ahí el dato vivirá en la base porque es
 * texto legal que él cambia sin desplegar. Que estén en dos sitios es a
 * propósito y temporal: la página de venta no puede esperar a que exista la
 * maquinaria de los legales para tener un teléfono.
 */

/**
 * El número, tal como se lee. Los espacios son de lectura, no de datos.
 *
 * Colombia: `+57` y nueve cifras. Se escribe con el `+` porque este producto
 * se vende en tres idiomas y en tres mercados: un número sin prefijo
 * internacional no se puede marcar desde fuera del país, y quien lo lee en
 * inglés o en portugués está, por definición, fuera.
 */
export const TELEFONO_LEIDO = "+57 311 4551047";

/** El correo al que escribe un cliente. */
export const CORREO_DE_CONTACTO = "sac@grupoefamaa.com";

/** La dirección, tal como se escribe en un documento. */
export const DIRECCION = "Cra. 7 #156-80, Torre 3 Oficina 1805 · Bogotá, Colombia";

/**
 * Solo las cifras, que es lo que quiere `wa.me`.
 *
 * Se saca del número leído en vez de escribirse aparte, y es la mitad del
 * porqué de este fichero: dos cadenas escritas a mano se separan a la primera
 * corrección, y la que se separa es la que nadie mira — el texto se ve bien y
 * el enlace lleva a otro sitio.
 */
export const TELEFONO_EN_CIFRAS = TELEFONO_LEIDO.replace(/[^0-9]/g, "");

/**
 * `wa.me` y no `api.whatsapp.com`: es el enlace corto oficial, abre la
 * aplicación si está instalada y la web si no, y no lleva parámetros que
 * cuenten nada de quien lo pulsa.
 *
 * Sin mensaje previo (`?text=`) a propósito. Un mensaje escrito por nosotros
 * en la caja de otra persona es poner palabras en su boca, y además el primero
 * que llega a soporte diciendo algo que el cliente no escribió confunde a las
 * dos partes.
 */
export const ENLACE_DE_WHATSAPP = `https://wa.me/${TELEFONO_EN_CIFRAS}`;

/*
  AQUÍ HABÍA UN `ENLACE_DE_TELEFONO` (`tel:`), Y NO LO USABA NADIE (10/09).

  El número que dio el dueño en D37 es de WhatsApp —«WhatsApp: +57 311
  4551047», y un botón de WhatsApp flotante—, no una línea para llamar. Un
  enlace `tel:` preparado es una invitación a poner «Llamar» en una pantalla y
  que el teléfono suene donde nadie lo coge. Si un día hay línea de voz, lo
  dice el dueño y se escribe entonces.
*/

/** Para un `<a href>` de correo. */
export const ENLACE_DE_CORREO = `mailto:${CORREO_DE_CONTACTO}`;

/**
 * El horario, en horas de veinticuatro y en la zona de Bogotá.
 *
 * NO va aquí la frase que se lee —«Lunes a Viernes de 8am a 6pm»— porque esa
 * hay que traducirla a tres idiomas y su sitio es el diccionario. Aquí van los
 * números, para que la frase de cada idioma no pueda decir otra hora que las
 * demás.
 *
 * Y lleva la zona escrita: «de 8 a 18» sin zona es una hora que cada quien lee
 * en la suya, y quien escribe desde Brasil a las 8:30 de su mañana se
 * encuentra con que aquí son las 6:30 y no hay nadie.
 */
export const HORARIO = {
  zona: "America/Bogota",
  desplazamiento: "UTC-5",
  entreSemana: { abre: 8, cierra: 18 },
  sabado: { abre: 8, cierra: 12 },
} as const;
